/**
 * QR コード符号化（JIS X 0510 / ISO-IEC 18004 の Model 2）。純 TS・依存なし。
 *
 * 用途は共有 URL 1 本だけなので、必要な部分に絞ってある:
 *   - モードはバイト（8bit）のみ。URL には数字・英数モードが効かない
 *     （`/` `?` `#` と小文字が英数集合の外にあるため）。
 *   - 誤り訂正レベルは M（15%）固定。画面表示にはこれで足りる。
 *   - バージョンは 1〜10（v10-M でバイト容量 213 — 共有 URL には十分）。
 *
 * サイズを抑えるため、静的な表はバージョン別の 3 数値だけに留め、
 * GF(256) の対数表・生成多項式・BCH 符号はすべて実行時に計算する。
 * マスクは 8 パターン全評価（固定マスクは読み取り率を落とすので削らない）。
 */

export interface QrMatrix {
  /** 一辺のモジュール数。 */
  size: number;
  /** 行優先の 0/1（1 = 暗）。長さは size * size。 */
  modules: Uint8Array;
}

/**
 * 誤り訂正レベル M のバージョン別パラメータ。
 * [データコード語数, ブロックあたりの誤り訂正コード語数, ブロック数]
 *
 * ブロックの長短分割は規格表を持たずに導ける:
 *   短ブロック長 = floor(データ / ブロック数)、長ブロック = 余り個だけ +1。
 * 例 v8-M: 154 / 4 = 38 余り 2 → 38×2 + 39×2（規格表と一致）。
 */
const VERSIONS: readonly (readonly [number, number, number])[] = [
  [16, 10, 1], // v1
  [28, 16, 1], // v2
  [44, 26, 1], // v3
  [64, 18, 2], // v4
  [86, 24, 2], // v5
  [108, 16, 4], // v6
  [124, 18, 4], // v7
  [154, 22, 4], // v8
  [182, 22, 5], // v9
  [216, 26, 5], // v10
];

/** 誤り訂正レベル M の 2 ビット表現（形式情報用）。 */
const EC_LEVEL_M = 0b00;
/** バイトモードのモード指示子。 */
const MODE_BYTE = 0b0100;
/** 埋め草コード語（規格で交互に置くと定められている）。 */
const PAD_CODEWORDS = [0xec, 0x11];

/**
 * テキストを QR の 0/1 行列へ符号化する。
 * v10-M の容量（213 バイト）を超える場合は null（呼び出し側で QR を省く）。
 */
export function encodeQr(text: string): QrMatrix | null {
  const data = new TextEncoder().encode(text);
  const version = chooseVersion(data.length);
  if (version === null) return null;

  const codewords = interleave(version, buildCodewords(version, data));
  return render(version, codewords);
}

/** 収まる最小のバージョン。どれにも入らなければ null。 */
function chooseVersion(byteLength: number): number | null {
  for (let version = 1; version <= VERSIONS.length; version += 1) {
    if (byteLength <= byteCapacity(version)) return version;
  }
  return null;
}

/** バイトモードで格納できるバイト数。 */
function byteCapacity(version: number): number {
  const [dataCodewords] = params(version);
  return Math.floor((dataCodewords * 8 - 4 - countBits(version)) / 8);
}

/** 文字数指示子のビット数（バイトモード）。v10 以上は 16 ビット。 */
function countBits(version: number): number {
  return version < 10 ? 8 : 16;
}

function params(version: number): readonly [number, number, number] {
  const row = VERSIONS[version - 1];
  if (!row) throw new Error(`未対応のバージョン: ${version}`);
  return row;
}

function sizeOf(version: number): number {
  return version * 4 + 17;
}

// -- データ符号化 -----------------------------------------------------------

/** ビット列を組み、埋め草まで詰めたデータコード語を返す。 */
function buildCodewords(version: number, data: Uint8Array): Uint8Array {
  const [dataCodewords] = params(version);
  const bits: number[] = [];
  const push = (value: number, length: number): void => {
    for (let i = length - 1; i >= 0; i -= 1) bits.push((value >> i) & 1);
  };

  push(MODE_BYTE, 4);
  push(data.length, countBits(version));
  for (const byte of data) push(byte, 8);

  // 終端子は最大 4 ビット（容量が足りなければ短くてよい）
  const capacity = dataCodewords * 8;
  for (let i = 0; i < 4 && bits.length < capacity; i += 1) bits.push(0);
  while (bits.length % 8 !== 0) bits.push(0);

  const out = new Uint8Array(dataCodewords);
  for (let i = 0; i < bits.length; i += 8) {
    let byte = 0;
    for (let b = 0; b < 8; b += 1) byte = (byte << 1) | (bits[i + b] ?? 0);
    out[i / 8] = byte;
  }
  for (let i = bits.length / 8; i < dataCodewords; i += 1) {
    out[i] = PAD_CODEWORDS[(i - bits.length / 8) % 2] as number;
  }
  return out;
}

/** ブロックへ分割し、誤り訂正語を付けてから規格どおりに交互配置する。 */
function interleave(version: number, data: Uint8Array): Uint8Array {
  const [dataCodewords, ecPerBlock, blockCount] = params(version);
  const shortLength = Math.floor(dataCodewords / blockCount);
  const longCount = dataCodewords % blockCount;

  const dataBlocks: Uint8Array[] = [];
  const ecBlocks: Uint8Array[] = [];
  let offset = 0;
  for (let b = 0; b < blockCount; b += 1) {
    const length = shortLength + (b >= blockCount - longCount ? 1 : 0);
    const block = data.subarray(offset, offset + length);
    offset += length;
    dataBlocks.push(block);
    ecBlocks.push(reedSolomon(block, ecPerBlock));
  }

  const out = new Uint8Array(dataCodewords + ecPerBlock * blockCount);
  let at = 0;
  for (let i = 0; i <= shortLength; i += 1) {
    for (const block of dataBlocks) {
      if (i < block.length) out[at++] = block[i] as number;
    }
  }
  for (let i = 0; i < ecPerBlock; i += 1) {
    for (const block of ecBlocks) out[at++] = block[i] as number;
  }
  return out;
}

// -- GF(256) ---------------------------------------------------------------

/** 原始多項式 0x11D による指数表・対数表（初回に構築して使い回す）。 */
let gfExp: Uint8Array | null = null;
let gfLog: Uint8Array | null = null;

function gfTables(): { exp: Uint8Array; log: Uint8Array } {
  if (!gfExp || !gfLog) {
    const exp = new Uint8Array(512);
    const log = new Uint8Array(256);
    let value = 1;
    for (let i = 0; i < 255; i += 1) {
      exp[i] = value;
      log[value] = i;
      value <<= 1;
      if (value & 0x100) value ^= 0x11d;
    }
    for (let i = 255; i < 512; i += 1) exp[i] = exp[i - 255] as number;
    gfExp = exp;
    gfLog = log;
  }
  return { exp: gfExp, log: gfLog };
}

function gfMultiply(a: number, b: number): number {
  if (a === 0 || b === 0) return 0;
  const { exp, log } = gfTables();
  return exp[(log[a] as number) + (log[b] as number)] as number;
}

/** 誤り訂正コード語（生成多項式も都度計算する — 表を持たない）。 */
function reedSolomon(data: Uint8Array, ecLength: number): Uint8Array {
  const { exp } = gfTables();

  // 生成多項式 (x - a^0)(x - a^1)...(x - a^(ecLength-1))
  const generator = new Uint8Array(ecLength + 1);
  generator[0] = 1;
  let degree = 0;
  for (let i = 0; i < ecLength; i += 1) {
    degree += 1;
    for (let j = degree; j > 0; j -= 1) {
      generator[j] = (generator[j] as number) ^ gfMultiply(generator[j - 1] as number, exp[i] as number);
    }
  }

  const remainder = new Uint8Array(ecLength);
  for (const byte of data) {
    const factor = byte ^ (remainder[0] as number);
    remainder.copyWithin(0, 1);
    remainder[ecLength - 1] = 0;
    if (factor !== 0) {
      for (let i = 0; i < ecLength; i += 1) {
        remainder[i] = (remainder[i] as number) ^ gfMultiply(generator[i + 1] as number, factor);
      }
    }
  }
  return remainder;
}

// -- 行列の組み立て ---------------------------------------------------------

interface Canvas {
  size: number;
  modules: Uint8Array;
  /** 機能パターン（データを置けない位置）。 */
  reserved: Uint8Array;
}

function render(version: number, codewords: Uint8Array): QrMatrix {
  const size = sizeOf(version);
  const canvas: Canvas = {
    size,
    modules: new Uint8Array(size * size),
    reserved: new Uint8Array(size * size),
  };

  placeFunctionPatterns(canvas, version);
  placeData(canvas, codewords);

  return { size, modules: chooseMask(canvas) };
}

function set(canvas: Canvas, row: number, col: number, dark: boolean, isFunction: boolean): void {
  if (row < 0 || col < 0 || row >= canvas.size || col >= canvas.size) return;
  canvas.modules[row * canvas.size + col] = dark ? 1 : 0;
  if (isFunction) canvas.reserved[row * canvas.size + col] = 1;
}

function placeFunctionPatterns(canvas: Canvas, version: number): void {
  const { size } = canvas;

  // 位置検出パターン（3 隅）と分離パターン
  for (const [r0, c0] of [
    [0, 0],
    [size - 7, 0],
    [0, size - 7],
  ] as const) {
    for (let dr = -1; dr <= 7; dr += 1) {
      for (let dc = -1; dc <= 7; dc += 1) {
        const ring = dr === 0 || dr === 6 || dc === 0 || dc === 6;
        const core = dr >= 2 && dr <= 4 && dc >= 2 && dc <= 4;
        const inside = dr >= 0 && dr <= 6 && dc >= 0 && dc <= 6;
        set(canvas, r0 + dr, c0 + dc, inside && (ring || core), true);
      }
    }
  }

  // 位置合わせパターン。v1〜10 では中心座標が式で出る（表は不要）:
  //   v2〜6 → [6, size-7] / v7〜10 → [6, 中点, size-7]
  // （v11 以降はこの規則が崩れるので、対応バージョンを増やすときは表が要る）
  for (const centerRow of alignmentCenters(version, size)) {
    for (const centerCol of alignmentCenters(version, size)) {
      // 位置検出パターンと重なる 3 箇所は置かない
      const atFinder =
        (centerRow === 6 && centerCol === 6) ||
        (centerRow === 6 && centerCol === size - 7) ||
        (centerRow === size - 7 && centerCol === 6);
      if (atFinder) continue;
      for (let dr = -2; dr <= 2; dr += 1) {
        for (let dc = -2; dc <= 2; dc += 1) {
          const dark = Math.abs(dr) === 2 || Math.abs(dc) === 2 || (dr === 0 && dc === 0);
          set(canvas, centerRow + dr, centerCol + dc, dark, true);
        }
      }
    }
  }

  // タイミングパターン
  for (let i = 8; i < size - 8; i += 1) {
    set(canvas, 6, i, i % 2 === 0, true);
    set(canvas, i, 6, i % 2 === 0, true);
  }

  // 形式情報の領域を予約（値はマスク確定後に書く）。
  // 添字 6 はタイミングパターンであって形式情報ではない — ここを潰すと
  // タイミングが途切れ、スキャナが格子を復元できなくなる。
  for (let i = 0; i <= 8; i += 1) {
    if (i === 6) continue;
    set(canvas, 8, i, false, true);
    set(canvas, i, 8, false, true);
  }
  for (let i = 0; i < 8; i += 1) {
    set(canvas, 8, size - 1 - i, false, true);
    set(canvas, size - 1 - i, 8, false, true);
  }
  // 常に暗のモジュール
  set(canvas, size - 8, 8, true, true);

  if (version >= 7) {
    const bits = versionBits(version);
    for (let i = 0; i < 18; i += 1) {
      const dark = ((bits >> i) & 1) === 1;
      const a = Math.floor(i / 3);
      const b = size - 11 + (i % 3);
      set(canvas, a, b, dark, true);
      set(canvas, b, a, dark, true);
    }
  }
}

function alignmentCenters(version: number, size: number): number[] {
  if (version === 1) return [];
  const last = size - 7;
  if (version < 7) return [6, last];
  return [6, (6 + last) / 2, last];
}

/** 右下から蛇行しながら 2 列ずつ埋める（タイミング列は飛ばす）。 */
function placeData(canvas: Canvas, codewords: Uint8Array): void {
  const { size } = canvas;
  const totalBits = codewords.length * 8;
  let bitIndex = 0;
  let upward = true;

  for (let col = size - 1; col > 0; col -= 2) {
    if (col === 6) col -= 1; // タイミングパターンの列
    for (let i = 0; i < size; i += 1) {
      const row = upward ? size - 1 - i : i;
      for (const c of [col, col - 1]) {
        if (canvas.reserved[row * size + c]) continue;
        let dark = false;
        if (bitIndex < totalBits) {
          const byte = codewords[bitIndex >> 3] as number;
          dark = ((byte >> (7 - (bitIndex & 7))) & 1) === 1;
        }
        // 余剰ビットは 0（暗くしない）
        canvas.modules[row * size + c] = dark ? 1 : 0;
        bitIndex += 1;
      }
    }
    upward = !upward;
  }
}

/** 8 パターンすべてを評価し、罰点が最小のものを採る。 */
function chooseMask(canvas: Canvas): Uint8Array {
  const { size } = canvas;
  let best: Uint8Array | null = null;
  let bestPenalty = Infinity;

  for (let mask = 0; mask < 8; mask += 1) {
    const candidate = new Uint8Array(canvas.modules);
    for (let row = 0; row < size; row += 1) {
      for (let col = 0; col < size; col += 1) {
        if (canvas.reserved[row * size + col]) continue;
        const at = row * size + col;
        if (maskAt(mask, row, col)) candidate[at] = (candidate[at] as number) ^ 1;
      }
    }
    writeFormat(candidate, size, mask);

    const penalty = penaltyOf(candidate, size);
    if (penalty < bestPenalty) {
      bestPenalty = penalty;
      best = candidate;
    }
  }
  return best as Uint8Array;
}

function maskAt(mask: number, row: number, col: number): boolean {
  switch (mask) {
    case 0:
      return (row + col) % 2 === 0;
    case 1:
      return row % 2 === 0;
    case 2:
      return col % 3 === 0;
    case 3:
      return (row + col) % 3 === 0;
    case 4:
      return (Math.floor(row / 2) + Math.floor(col / 3)) % 2 === 0;
    case 5:
      return ((row * col) % 2) + ((row * col) % 3) === 0;
    case 6:
      return (((row * col) % 2) + ((row * col) % 3)) % 2 === 0;
    default:
      return (((row + col) % 2) + ((row * col) % 3)) % 2 === 0;
  }
}

/** 形式情報（誤り訂正レベル 2 ビット + マスク 3 ビット）を BCH(15,5) で符号化して置く。 */
function writeFormat(modules: Uint8Array, size: number, mask: number): void {
  const bits = formatBits(mask);
  const put = (row: number, col: number, index: number): void => {
    modules[row * size + col] = (bits >> index) & 1;
  };

  // 15 ビットを 2 か所へ。縦（列 8）は上から、横（行 8）は右から番号順に並ぶ。
  // 添字 6・8 の飛びはタイミングパターンを避けるため。
  for (let i = 0; i < 15; i += 1) {
    put(i < 6 ? i : i < 8 ? i + 1 : size - 15 + i, 8, i);
    put(8, i < 8 ? size - 1 - i : i === 8 ? 7 : 14 - i, i);
  }

  modules[(size - 8) * size + 8] = 1; // 常に暗
}

export function formatBits(mask: number): number {
  const value = (EC_LEVEL_M << 3) | mask;
  let bch = value << 10;
  for (let i = 4; i >= 0; i -= 1) {
    if ((bch >> (10 + i)) & 1) bch ^= 0b10100110111 << i;
  }
  return ((value << 10) | bch) ^ 0b101010000010010;
}

export function versionBits(version: number): number {
  let bch = version << 12;
  for (let i = 5; i >= 0; i -= 1) {
    if ((bch >> (12 + i)) & 1) bch ^= 0b1111100100101 << i;
  }
  return (version << 12) | bch;
}

// -- マスクの罰点 -----------------------------------------------------------

/** 規格の 4 規則。読み取りやすさの評価なので削らない。 */
function penaltyOf(modules: Uint8Array, size: number): number {
  const at = (row: number, col: number): number => modules[row * size + col] as number;
  let penalty = 0;

  // 規則 1: 同色 5 連以上
  for (let i = 0; i < size; i += 1) {
    for (const read of [
      (k: number): number => at(i, k),
      (k: number): number => at(k, i),
    ]) {
      let run = 1;
      for (let k = 1; k < size; k += 1) {
        if (read(k) === read(k - 1)) {
          run += 1;
          if (run === 5) penalty += 3;
          else if (run > 5) penalty += 1;
        } else {
          run = 1;
        }
      }
    }
  }

  // 規則 2: 2x2 の同色ブロック
  for (let row = 0; row < size - 1; row += 1) {
    for (let col = 0; col < size - 1; col += 1) {
      const v = at(row, col);
      if (v === at(row, col + 1) && v === at(row + 1, col) && v === at(row + 1, col + 1)) {
        penalty += 3;
      }
    }
  }

  // 規則 3: 位置検出パターンに紛らわしい 1:1:3:1:1 の並び
  const forward = [1, 0, 1, 1, 1, 0, 1, 0, 0, 0, 0];
  const backward = [0, 0, 0, 0, 1, 0, 1, 1, 1, 0, 1];
  for (let i = 0; i < size; i += 1) {
    for (let k = 0; k + 11 <= size; k += 1) {
      for (const read of [
        (offset: number): number => at(i, k + offset),
        (offset: number): number => at(k + offset, i),
      ]) {
        let matchesForward = true;
        let matchesBackward = true;
        for (let m = 0; m < 11; m += 1) {
          const value = read(m);
          if (value !== forward[m]) matchesForward = false;
          if (value !== backward[m]) matchesBackward = false;
        }
        if (matchesForward) penalty += 40;
        if (matchesBackward) penalty += 40;
      }
    }
  }

  // 規則 4: 暗モジュールの偏り
  let dark = 0;
  for (const value of modules) dark += value;
  const percent = (dark * 100) / (size * size);
  penalty += Math.floor(Math.abs(percent - 50) / 5) * 10;

  return penalty;
}
